<script setup>
import { useCustomerStore } from "@/stores/CustomersStore"
import { useDisplay } from "vuetify"
import { createValidationRules } from "@/utils/validationRules"
import { formatNumber } from "@/utils/strings"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"

const emits = defineEmits(['update:tabs'])
const { mobile } = useDisplay()
const { t } = useI18n()
const customerStore = useCustomerStore()
const calendarStore = useCalendarStore()

const pointsModal = ref(false)

const loyaltyPoints = ref(customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints)
const validationRules = createValidationRules(t)

const updatetLoyaltyPoints = async () => {
  if(!loyaltyPoints.value) {
    showToast(t('actions.form.fill_field'), 'warning')

    return
  }

  const result = await customerStore.updateCustomerLoyaltyPoints(customerStore.selectedCustomer.salonerClientId, Number(loyaltyPoints.value || 0))

  if(result) {
    // Refetch customer information to update the UI
    await customerStore.getCustomerInformation(customerStore.selectedCustomer?.salonerClientId)
    pointsModal.value = false
  }
}
</script>

<template>
  <VList
    density="compact"
    variant="outlined"
    :class="mobile? 'text-body-2 text-high-emphasis' : ''"
  >
    <VListItem class="rounded-t-lg">
      <template #title>
        <span>
          {{ $t('amountOfVisitsMade') }}
        </span>
      </template>
      <template #append>
        <span class="number">{{ customerStore.gatheredUserInfo?.stats?.finished || 0 }}</span>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('amountOfCancellations') }}
        </span>
      </template>
      <template #append>
        <span class="number">{{ customerStore.gatheredUserInfo?.stats?.canceled || 0 }}</span>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('amountOfNoShows') }}
        </span>
      </template>
      <template #append>
        <span class="number">{{ customerStore.gatheredUserInfo?.stats?.noShow || 0 }}</span>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('noShowsRatio') }}
        </span>
      </template>
      <template #append>
        <div class="number">
          {{ customerStore.gatheredUserInfo?.stats?.factor || 0 }}%
          <span>
            <VIcon
              icon="ri-user-unfollow-fill"
              color="error"
            />
            <VTooltip
              activator="parent"
              open-on-click
            >
              {{ $t('amountOfAllVisits') }}: {{ customerStore.gatheredUserInfo?.stats?.allVisits || 0 }}, <br>
              {{ $t('amountOfNoShows10') }}: {{ customerStore.gatheredUserInfo?.stats?.noShow || 0 }}
            </VTooltip>
          </span>
        </div>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('theMostPopularService') }}
        </span>
      </template>
      <template #append>
        <span class="number">{{ customerStore.gatheredUserInfo?.stats?.popularService || $t('no_data') }}</span>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('loyaltyProgram') }}
        </span>
      </template>
      <template #append>
        <div class="d-flex align-center ga-2 number">
          <span>
            {{ formatNumber(customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints) || 0 }} {{ $t('pts') }}.
            <VTooltip activator="parent">
              {{ $t('collectedPoints') }}
            </VTooltip>
          </span>

          <VBtn
            v-if="!calendarStore.viewSettings.calendarReadOnly"
            :disabled="customerStore.isLoading"
            size="x-small"
            class="pr-0"
            variant="text"
            color="primary"
            @click="pointsModal= true"
          >
            <VIcon
              size="23"
              icon="ri-pencil-line"
              class="cursor-pointer ml-n2"
            />
            <VTooltip activator="parent">
              {{ $t('update') }}
            </VTooltip>
          </VBtn>
        </div>
      </template>
    </VListItem>
    <VListItem>
      <template #title>
        <span>
          {{ $t('vouchersOwned') }}
        </span>
      </template>
      <template #append>
        <div class="number">
          {{ customerStore.voucherCount || 0 }}
          <VIcon
            class="cursor-pointer ml-2"
            size="small"
            color="primary"
            icon="ri-search-line"
            @click="$emit('update:tabs', 3)"
          />
          <VTooltip activator="parent">
            {{ $t('more') }}
          </VTooltip>
        </div>
      </template>
    </VListItem>
    <VListItem class="rounded-b-lg">
      <template #title>
        <span>
          {{ $t('ticketsOwned') }}
        </span>
      </template>
      <template #append>
        <div class="number">
          {{ customerStore.ticketCount || 0 }}
          <VIcon
            class="cursor-pointer ml-2"
            size="small"
            color="primary"
            icon="ri-search-line"
            @click="$emit('update:tabs', 4)"
          />
          <VTooltip activator="parent">
            {{ $t('more') }}
          </VTooltip>
        </div>
      </template>
    </VListItem>
  </VList>

  <!-- Dialog -->
  <VDialog
    v-if="pointsModal"
    v-model="pointsModal"
    :persistent="customerStore.isLoading"
    max-width="300px"
  >
    <VCard
      :disabled="customerStore.isLoading"
      :loading="customerStore.isLoading"
    >
      <template #title>
        <div class="d-flex align-center justify-space-between">
          {{ $t('loyaltyProgram') }}
          <VIcon
            icon="ri-close-line"
            class="cursor-pointer"
            @click="pointsModal = false; loyaltyPoints = customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints;"
          />
        </div>
      </template>
      <VCardText>
        <p class="xs-font-size">
          {{ $t('currentState') }}: {{ customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints }} {{ $t('pts') }}
        </p>
        <VTextField
          v-model="loyaltyPoints"
          type="number"
          :label="$t('pointsNumber')"
          color="secondary"
          density="compact"
          :rules="[validationRules.nonNegativeNumberRules[0]]"
          @keydown="e => e.key === '.' && e.preventDefault()"
        />
      </VCardText>
      <VCardActions>
        <div class="d-flex justify-space-between w-100 pa-2">
          <VBtn
            variant="outlined"
            color="secondary"
            @click.stop="pointsModal = false; loyaltyPoints = customerStore.gatheredUserInfo?.loyaltyProgram.loyaltyPoints;"
          >
            {{ $t('cancel') }}
          </VBtn>
          <VBtn
            variant="flat"
            :disabled="loyaltyPoints < 0"
            color="green"
            @click.stop="updatetLoyaltyPoints"
          >
            {{ $t('update') }}
          </VBtn>
        </div>
      </VCardActions>
    </VCard>
  </VDialog>
</template>


